import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { ArrowLeft, Plus, Check, Trash2 } from "lucide-react";
import Attachments from "@/components/Attachments";

const PRIORITY = {
  urgent: "bg-destructive text-white", high: "bg-orange-500 text-white",
  medium: "bg-slate-200 text-slate-700", low: "bg-slate-100 text-slate-500",
};

export default function ProjectDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [project, setProject] = useState(null);
  const [title, setTitle] = useState("");

  const load = () => api.get(`/projects/${id}`).then((r) => setProject(r.data));
  useEffect(() => { load(); }, [id]);

  const addTask = async () => {
    if (!title.trim()) return;
    await api.post("/tasks", { title, project_id: id, priority: "medium" });
    setTitle("");
    load();
  };
  const toggle = async (t) => {
    await api.put(`/tasks/${t.id}`, { status: t.status === "done" ? "todo" : "done" });
    load();
  };
  const remove = async (tid) => { await api.delete(`/tasks/${tid}`); load(); };
  const del = async () => { await api.delete(`/projects/${id}`); toast.success("Project deleted"); navigate("/projects"); };

  if (!project) return <div className="label-mono text-muted-foreground">Loading…</div>;

  return (
    <div className="space-y-6" data-testid="project-detail">
      <button onClick={() => navigate("/projects")} className="flex items-center gap-2 label-mono text-slate-500 hover:text-slate-950">
        <ArrowLeft size={14} /> Projects
      </button>
      <div className="flex items-start justify-between gap-4">
        <div>
          <div className="w-10 h-10 rounded-sm mb-3" style={{ background: project.color }} />
          <h1 className="text-4xl font-black tracking-tighter">{project.name}</h1>
          <p className="text-slate-600 mt-2 max-w-2xl">{project.description}</p>
        </div>
        <Button variant="outline" className="rounded-sm text-destructive border-destructive/30 hover:bg-destructive/10" onClick={del} data-testid="delete-project-button">
          <Trash2 size={16} className="mr-2" /> Delete
        </Button>
      </div>

      <Card className="rounded-sm border-border shadow-none p-5">
        <h3 className="text-xl font-bold tracking-tight mb-4">Tasks</h3>
        <div className="flex gap-2 mb-4">
          <Input placeholder="Add a task to this project…" value={title}
            onChange={(e) => setTitle(e.target.value)} onKeyDown={(e) => e.key === "Enter" && addTask()}
            className="rounded-sm" data-testid="project-task-input" />
          <Button onClick={addTask} className="rounded-sm bg-slate-950 hover:bg-slate-800" data-testid="project-add-task"><Plus size={16} /></Button>
        </div>
        <div className="space-y-1">
          {project.tasks.length === 0 && <p className="text-sm text-muted-foreground">No tasks yet.</p>}
          {project.tasks.map((t) => (
            <div key={t.id} className="flex items-center gap-3 py-2.5 border-b border-border last:border-0 group">
              <button onClick={() => toggle(t)}
                className={`w-5 h-5 rounded-sm border flex items-center justify-center ${t.status === "done" ? "bg-slate-950 border-slate-950 text-white" : "border-slate-300"}`}
                data-testid={`toggle-task-${t.id}`}>
                {t.status === "done" && <Check size={12} />}
              </button>
              <span className={`flex-1 text-sm font-medium ${t.status === "done" ? "line-through text-slate-400" : ""}`}>{t.title}</span>
              <span className={`label-mono px-1.5 py-0.5 rounded-sm ${PRIORITY[t.priority]}`}>{t.priority}</span>
              <button onClick={() => remove(t.id)} className="opacity-0 group-hover:opacity-100 text-slate-400 hover:text-destructive">
                <Trash2 size={14} />
              </button>
            </div>
          ))}
        </div>
      </Card>

      <Card className="rounded-sm border-border shadow-none p-5">
        <Attachments parentType="project" parentId={id} />
      </Card>
    </div>
  );
}
